/**
 * CustomerContractsTab - Aba de contratos no modal premium de cliente
 * Exibe todos os contratos gerados nos negócios do cliente
 * Usa ContractsTableView compartilhado (mesmo padrão visual da aba Negócios)
 * 
 * @version 2.0.0
 * RELEASE: 2026-03-15 - Migração para layout de tabela unificado
 */
import React, { useState } from 'react';
import { useCustomerContracts } from '@/hooks/useCustomerContracts';
import { ContractsTableView } from '@/components/contracts/ContractsTableView';
import { ContractDetailSheet } from '@/components/contracts/ContractDetailSheet';

// Props do componente
interface CustomerContractsTabProps {
  /** ID do cliente */
  customerId: string | null;
  /** ID do contato principal (fallback para busca de deals) */
  contactId?: string | null;
  /** v2.2.0: contexto de veículo (aba Contratos do modal de veículo) */
  vehicleId?: string | null;
  /** Se a aba está ativa (lazy loading) */
  isActive?: boolean;
  /** v2.1.0: Permissão para editar contrato (repassada ao ContractDetailSheet) */
  canEdit?: boolean;
  /** v2.1.0: Permissão para excluir/cancelar contrato */
  canDelete?: boolean;
}

/**
 * Aba de contratos dentro do modal premium de cliente
 * Diferente do DealContractsTab: exibe coluna do negócio e não permite gerar contratos
 */
export function CustomerContractsTab({
  customerId,
  contactId,
  vehicleId = null,
  isActive = false,
  canEdit = true,
  canDelete = true,
}: CustomerContractsTabProps) {
  // Estado do sheet de detalhes do contrato
  const [selectedContractId, setSelectedContractId] = useState<string | null>(null);

  // Busca contratos de todos os deals do cliente
  const { data: contracts, isLoading } = useCustomerContracts(customerId, contactId, isActive, vehicleId);

  return (
    <>
      {/* Tabela compartilhada com coluna de negócio visível */}
      <ContractsTableView
        contracts={contracts}
        isLoading={isLoading}
        showDealColumn={true}
        onSelectContract={(id) => setSelectedContractId(id)}
      />

      {/* Sheet de detalhes do contrato (canEdit/canDelete repassados) */}
      <ContractDetailSheet
        contractId={selectedContractId}
        onClose={() => setSelectedContractId(null)}
        canEdit={canEdit}
        canDelete={canDelete}
      />
    </>
  );
}
